import { canEdit } from '../store.js';
import { getContext, refreshLiveData } from '../trip-context.js';
import {
  deleteInstanceBase,
  deleteInstanceStop,
  deleteWishlistItem,
  reorderInstanceStops,
  reorderWishlist,
  updateInstanceDay,
  upsertInstanceBase,
  upsertInstanceStop,
  upsertWishlistItem,
} from '../instance-itinerary.js';
import { formatDateIT, formatEur } from '../utils/format.js';
import { toDayKey } from '../utils/day-key.js';
import { escapeHtml } from '../utils/dom.js';
import { renderPlaceMetaSummary } from '../utils/place-search.js';
import {
  bindPlaceAutocomplete,
  parseCoordsFromForm,
  parsePlaceMetaFromForm,
  placeHiddenFieldsHtml,
} from '../utils/place-autocomplete.js';
import { bindPlaceEditor, placeCoordFieldsHtml } from '../utils/place-editor.js';
import { bindSortableList } from '../utils/sortable-list.js';
import { invalidateInstanceMapCache } from '../utils/map-cache.js';
import { resolveTripBases } from '../utils/trip-bases.js';
import { bindTripShell, refreshTripBases, renderTripShell } from './shell.js';

let customizeListenersAbort = null;

function scheduledTimeValue(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const h = String(d.getHours()).padStart(2, '0');
  const m = String(d.getMinutes()).padStart(2, '0');
  return `${h}:${m}`;
}

function scheduledAtFromDayTime(dayDate, timeStr) {
  if (!timeStr) return null;
  const d = new Date(`${dayDate}T${timeStr}:00`);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

function getActiveCustomizeTab() {
  const match = location.hash.match(/customize\/(stays|wishlist|days)/);
  return match?.[1] ?? 'stays';
}

function addTriggerHtml(label) {
  return `
    <button type="button" class="custom-add-trigger" data-show-add-form aria-expanded="false">
      <span class="custom-add-trigger__icon" aria-hidden="true">+</span>
      <span class="custom-add-trigger__label">${escapeHtml(label)}</span>
    </button>
  `;
}

function renderCustomizeNav(tripId, activeTab) {
  const tabs = [
    { id: 'stays', label: 'Alloggi' },
    { id: 'wishlist', label: 'Da visitare' },
    { id: 'days', label: 'Giorni' },
  ];
  return tabs
    .map(
      (tab) => `
      <a
        href="#/trip/${escapeHtml(tripId)}/customize/${tab.id}"
        class="customize-nav__link${tab.id === activeTab ? ' customize-nav__link--active' : ''}"
        aria-current="${tab.id === activeTab ? 'page' : 'false'}"
      >${tab.label}</a>
    `,
    )
    .join('');
}

function stayForm(base = null) {
  return `
    <form class="custom-form" data-form="stay">
      <input type="hidden" name="id" value="${escapeHtml(base?.id ?? '')}" />
      ${placeHiddenFieldsHtml({
        lat: base?.lat,
        lng: base?.lng,
        placeId: base?.placeId,
        placeMeta: base?.placeMeta ?? {},
      })}
      <label class="field">
        <span class="field-label">Nome (hotel, casa, BnB…)</span>
        <input class="field-input" name="name" required data-place-name value="${escapeHtml(base?.name ?? '')}" autocomplete="off" />
      </label>
      <label class="field">
        <span class="field-label">Sottotitolo (opz.)</span>
        <input class="field-input" name="subtitle" value="${escapeHtml(base?.subtitle ?? '')}" />
      </label>
      <div class="custom-form__row">
        <label class="field">
          <span class="field-label">Dal</span>
          <input class="field-input" name="dateStart" type="date" required value="${escapeHtml(base?.dateStart ?? '')}" />
        </label>
        <label class="field">
          <span class="field-label">Al</span>
          <input class="field-input" name="dateEnd" type="date" required value="${escapeHtml(base?.dateEnd ?? '')}" />
        </label>
      </div>
      <label class="field">
        <span class="field-label">Link Maps</span>
        <input class="field-input" name="mapsUrl" type="url" placeholder="https://maps.google.com/…" value="${escapeHtml(base?.mapsUrl ?? '')}" data-place-maps-url />
      </label>
      ${placeCoordFieldsHtml({ lat: base?.lat, lng: base?.lng })}
      <label class="field">
        <span class="field-label">Costo complessivo (€, opz.)</span>
        <input class="field-input" name="costEur" type="number" min="0" step="0.01" placeholder="es. 450,00" value="${base?.costEur != null ? escapeHtml(String(base.costEur)) : ''}" />
        <span class="field-hint">Registrato in Spese (categoria Alloggio) alla data di check-in.</span>
      </label>
      <div data-place-preview-anchor></div>
      <div class="custom-form__actions">
        <button type="submit" class="btn-primary">Salva alloggio</button>
        <button type="button" class="btn-ghost" data-cancel-stay-form>Annulla</button>
      </div>
    </form>
  `;
}

function stopForm(stop = null, dayDate = '') {
  const metaSummary = renderPlaceMetaSummary(stop?.place_meta ?? stop?.placeMeta);
  return `
    <form class="custom-form custom-form--compact" data-form="stop">
      <input type="hidden" name="id" value="${escapeHtml(stop?.id ?? '')}" />
      <input type="hidden" name="instanceDayId" value="${escapeHtml(stop?.instance_day_id ?? stop?.instanceDayId ?? '')}" />
      <input type="hidden" name="dayDate" value="${escapeHtml(dayDate)}" />
      ${placeHiddenFieldsHtml({
        lat: stop?.lat,
        lng: stop?.lng,
        placeId: stop?.place_id ?? stop?.placeId,
        placeMeta: stop?.place_meta ?? stop?.placeMeta ?? {},
      })}
      <label class="field">
        <span class="field-label">Cerca luogo (OpenStreetMap)</span>
        <input class="field-input" name="name" required data-place-name value="${escapeHtml(stop?.name ?? '')}" autocomplete="off" placeholder="Inizia a digitare…" />
      </label>
      ${metaSummary ? `<p class="custom-list__meta">${escapeHtml(metaSummary)}</p>` : ''}
      <label class="field">
        <span class="field-label">Orario (opz.)</span>
        <input class="field-input" name="scheduledTime" type="time" value="${scheduledTimeValue(stop?.scheduled_at)}" />
      </label>
      <label class="field">
        <span class="field-label">Descrizione</span>
        <textarea class="field-input field-textarea" name="description" rows="2">${escapeHtml(stop?.description ?? '')}</textarea>
      </label>
      <label class="field">
        <span class="field-label">Auto / tempo</span>
        <input class="field-input" name="carTime" value="${escapeHtml(stop?.car_time ?? stop?.carTime ?? '')}" />
      </label>
      <label class="field">
        <span class="field-label">Camminata</span>
        <input class="field-input" name="walkInfo" value="${escapeHtml(stop?.walk_info ?? stop?.walkInfo ?? '')}" />
      </label>
      <label class="field">
        <span class="field-label">Costi</span>
        <input class="field-input" name="costInfo" value="${escapeHtml(stop?.cost_info ?? stop?.costInfo ?? '')}" />
      </label>
      <label class="field">
        <span class="field-label">Maps URL</span>
        <input class="field-input" name="mapsUrl" type="url" placeholder="https://maps.google.com/…" value="${escapeHtml(stop?.maps_url ?? stop?.mapsUrl ?? '')}" data-place-maps-url />
      </label>
      ${placeCoordFieldsHtml({ lat: stop?.lat, lng: stop?.lng })}
      <label class="field">
        <span class="field-label">Avviso (opz.)</span>
        <input class="field-input" name="warning" value="${escapeHtml(stop?.warning ?? '')}" />
      </label>
      <div data-place-preview-anchor></div>
      <div class="custom-form__actions">
        <button type="submit" class="btn-primary">Salva tappa</button>
        <button type="button" class="btn-ghost" data-cancel-stop-form>Annulla</button>
      </div>
    </form>
  `;
}

function wishlistForm(item = null) {
  return `
    <form class="custom-form" data-form="wishlist">
      <input type="hidden" name="id" value="${escapeHtml(item?.id ?? '')}" />
      ${placeHiddenFieldsHtml({
        lat: item?.lat,
        lng: item?.lng,
        placeId: item?.placeId,
        placeMeta: item?.placeMeta ?? {},
      })}
      <label class="field">
        <span class="field-label">Luogo da visitare</span>
        <input class="field-input" name="name" required data-place-name value="${escapeHtml(item?.name ?? '')}" autocomplete="off" placeholder="Cerca su OpenStreetMap…" />
      </label>
      <label class="field">
        <span class="field-label">Link Maps (opz.)</span>
        <input class="field-input" name="mapsUrl" type="url" placeholder="https://maps.google.com/…" value="${escapeHtml(item?.mapsUrl ?? '')}" data-place-maps-url />
      </label>
      <div class="custom-form__row">
        <label class="field">
          <span class="field-label">Latitudine</span>
          <input class="field-input" name="latVisible" type="number" step="any" placeholder="46.05" value="${escapeHtml(item?.lat != null ? String(item.lat) : '')}" data-place-lat-visible />
        </label>
        <label class="field">
          <span class="field-label">Longitudine</span>
          <input class="field-input" name="lngVisible" type="number" step="any" placeholder="14.50" value="${escapeHtml(item?.lng != null ? String(item.lng) : '')}" data-place-lng-visible />
        </label>
      </div>
      <button type="button" class="btn-ghost btn-ghost--sm" data-place-map-picker>📍 Scegli sulla mappa</button>
      <label class="field">
        <span class="field-label">Note (opz.)</span>
        <textarea class="field-input field-textarea" name="notes" rows="2">${escapeHtml(item?.notes ?? '')}</textarea>
      </label>
      <label class="field field--checkbox">
        <input type="checkbox" name="showOnMap" ${item?.showOnMap !== false ? 'checked' : ''} />
        <span>Mostra sulla mappa</span>
      </label>
      <div data-place-preview-anchor></div>
      <div class="custom-form__actions">
        <button type="submit" class="btn-primary">${item?.id ? 'Salva' : 'Aggiungi'}</button>
        <button type="button" class="btn-ghost" data-cancel-wishlist-form>Annulla</button>
      </div>
    </form>
  `;
}

function renderListItemActions(editAttrs, deleteAttrs) {
  if (!canEdit()) return '';
  return `
    <div class="custom-list__item-actions">
      <button type="button" class="custom-icon-btn" title="Modifica" aria-label="Modifica" ${editAttrs}>
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M4 20h4l10.5-10.5a2.1 2.1 0 00-3-3L5 17v3z" stroke="currentColor" stroke-width="1.75" stroke-linejoin="round"/><path d="M13.5 6.5l3 3" stroke="currentColor" stroke-width="1.75" stroke-linecap="round"/></svg>
      </button>
      <button type="button" class="custom-icon-btn custom-icon-btn--danger" title="Elimina" aria-label="Elimina" ${deleteAttrs}>
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M4 7h16M9 7V5h6v2M7 7l1 12h8l1-12" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"/></svg>
      </button>
    </div>
  `;
}

function renderStaysSection(ctx) {
  const bases = resolveTripBases(ctx.live);
  const rows = bases
    .map(
      (b) => `
      <li class="custom-list__item custom-list__item--card" data-stay-id="${b.id}">
        <div class="custom-list__body">
          <strong class="custom-list__title">${escapeHtml(b.name)}</strong>
          <span class="custom-list__meta">${escapeHtml(b.subtitle ?? '')}${b.subtitle ? ' · ' : ''}${formatDateIT(b.dateStart)} – ${formatDateIT(b.dateEnd)}${b.costEur != null ? ` · ${formatEur(b.costEur)}` : ''}</span>
        </div>
        ${renderListItemActions(`data-edit-stay="${b.id}"`, `data-delete-stay="${b.id}"`)}
      </li>
    `,
    )
    .join('');

  return `
    <section class="custom-section custom-section--stays" id="stays">
      <header class="custom-section__head">
        <h2 class="custom-section__title">Alloggi</h2>
        <p class="custom-section__hint">Specifici per il tuo gruppo — non modificano il template pubblico.</p>
      </header>
      <ul class="custom-list custom-list--cards">${rows || '<li class="custom-list__empty">Nessun alloggio ancora</li>'}</ul>
      ${canEdit() ? `
        <div class="custom-section__add" data-add-area="stay">
          <div id="stay-editor" class="custom-editor" hidden></div>
          ${addTriggerHtml('Aggiungi alloggio')}
        </div>
      ` : ''}
    </section>
  `;
}

function collectScheduledStops(days) {
  const groups = [];
  for (const day of days) {
    const stops = day.stops ?? [];
    if (!stops.length) continue;
    groups.push({
      dayId: day.id,
      dayKey: toDayKey(day.date),
      date: day.date,
      title: day.title,
      stops,
    });
  }
  return groups;
}

function renderScheduledStopRow(stop, { tripId, dayKey, dayTitle, date }) {
  const time = stop.scheduled_at ? scheduledTimeValue(stop.scheduled_at) : '';
  return `
    <li class="custom-list__item custom-list__item--card custom-list__item--readonly">
      <div class="custom-list__body">
        <strong class="custom-list__title">${escapeHtml(stop.name)}</strong>
        <span class="custom-list__meta">${escapeHtml(formatDateIT(date))} · ${escapeHtml(dayTitle)}${time ? ` · 🕐 ${escapeHtml(time)}` : ''}</span>
      </div>
      <a class="btn-ghost btn-ghost--sm" href="#/trip/${escapeHtml(tripId)}/customize/days">Giorni</a>
    </li>
  `;
}

function renderWishlistSection(ctx) {
  const items = ctx.live.wishlist ?? [];
  const scheduledGroups = collectScheduledStops(ctx.days);
  const scheduledCount = scheduledGroups.reduce((n, g) => n + g.stops.length, 0);

  const scheduledRows = scheduledGroups
    .flatMap((group) =>
      group.stops.map((stop) =>
        renderScheduledStopRow(stop, {
          tripId: ctx.tripId,
          dayKey: group.dayKey,
          dayTitle: group.title,
          date: group.date,
        }),
      ),
    )
    .join('');

  const wishlistRows = items
    .map(
      (item) => `
      <li class="custom-list__item custom-list__item--card sortable-item" data-sortable-id="${item.id}" data-wishlist-id="${item.id}">
        ${canEdit() ? '<button type="button" class="sortable-handle" data-sortable-handle aria-label="Riordina">⋮⋮</button>' : ''}
        <div class="custom-list__body">
          <strong class="custom-list__title">${escapeHtml(item.name)}</strong>
          ${item.notes ? `<span class="custom-list__meta">${escapeHtml(item.notes)}</span>` : ''}
          ${renderPlaceMetaSummary(item.placeMeta) ? `<span class="custom-list__meta">${escapeHtml(renderPlaceMetaSummary(item.placeMeta))}</span>` : ''}
          ${!item.showOnMap ? '<span class="custom-list__badge">Nascosto sulla mappa</span>' : ''}
        </div>
        ${renderListItemActions(`data-edit-wishlist="${item.id}"`, `data-delete-wishlist="${item.id}"`)}
      </li>
    `,
    )
    .join('');

  return `
    <section class="custom-section custom-section--wishlist" id="wishlist">
      <header class="custom-section__head">
        <h2 class="custom-section__title">Da visitare</h2>
        <p class="custom-section__hint">Tutti i luoghi del viaggio — in programma e idee da pianificare.</p>
      </header>

      <div class="custom-subsection">
        <h3 class="custom-subsection__title">In programma <span class="custom-subsection__count">${scheduledCount}</span></h3>
        <ul class="custom-list custom-list--cards">${scheduledRows || '<li class="custom-list__empty">Nessuna tappa in calendario</li>'}</ul>
      </div>

      <div class="custom-subsection">
        <h3 class="custom-subsection__title">Da pianificare <span class="custom-subsection__count">${items.length}</span></h3>
        <p class="custom-subsection__hint">Idee non ancora assegnate a un giorno — trascina ⋮⋮ per riordinare.</p>
        <ul class="custom-list custom-list--cards custom-list--sortable" id="wishlist-sortable">${wishlistRows || '<li class="custom-list__empty">Nessun luogo in lista</li>'}</ul>
        ${canEdit() ? `
          <div class="custom-section__add" data-add-area="wishlist">
            <div id="wishlist-editor" class="custom-editor" hidden></div>
            ${addTriggerHtml('Aggiungi luogo')}
          </div>
        ` : ''}
      </div>
    </section>
  `;
}

function renderDaysSection(ctx) {
  const dayBlocks = ctx.days
    .map((day) => {
      const stops = (day.stops ?? [])
        .map(
          (stop) => `
          <li class="custom-list__item custom-list__item--card sortable-item" data-sortable-id="${stop.id}" data-stop-id="${stop.id}">
            ${canEdit() ? '<button type="button" class="sortable-handle" data-sortable-handle aria-label="Riordina tappa">⋮⋮</button>' : ''}
            <div class="custom-list__body">
              <strong class="custom-list__title">${escapeHtml(stop.name)}</strong>
              ${stop.scheduled_at ? `<span class="custom-list__meta">🕐 ${escapeHtml(scheduledTimeValue(stop.scheduled_at))}</span>` : ''}
              ${stop.description ? `<span class="custom-list__meta">${escapeHtml(stop.description.slice(0, 80))}${stop.description.length > 80 ? '…' : ''}</span>` : ''}
            </div>
            ${renderListItemActions(`data-edit-stop="${stop.id}" data-day-id="${day.id}"`, `data-delete-stop="${stop.id}"`)}
          </li>
        `,
        )
        .join('');

      return `
        <article class="custom-day" data-day-id="${day.id}">
          <header class="custom-day__header">
            <div class="custom-day__heading">
              <time class="custom-day__date" datetime="${day.date}">${formatDateIT(day.date)}</time>
              <h3 class="custom-day__title">${escapeHtml(day.title)}</h3>
            </div>
            ${canEdit() ? `<button type="button" class="btn-ghost btn-ghost--sm" data-edit-day="${day.id}">Modifica giorno</button>` : ''}
          </header>
          ${canEdit() ? `
            <form class="custom-form custom-form--day" data-day-form="${day.id}" hidden>
              <label class="field">
                <span class="field-label">Titolo giornata</span>
                <input class="field-input" name="title" value="${escapeHtml(day.title)}" />
              </label>
              <label class="field">
                <span class="field-label">Ritmo</span>
                <textarea class="field-input field-textarea" name="ritmo" rows="2">${escapeHtml(day.ritmo ?? '')}</textarea>
              </label>
              <button type="submit" class="btn-primary btn-primary--sm">Salva giorno</button>
            </form>
          ` : ''}
          <ul class="custom-list custom-list--cards custom-list--nested custom-list--sortable" data-sort-day="${day.id}">${stops || '<li class="custom-list__empty">Nessuna tappa</li>'}</ul>
          ${canEdit() ? `
            <div class="custom-day__add" data-add-area="stop" data-day-id="${day.id}">
              <div class="stop-editor-slot custom-editor" data-day-id="${day.id}" hidden></div>
              ${addTriggerHtml('Aggiungi tappa')}
            </div>
          ` : ''}
        </article>
      `;
    })
    .join('');

  return `
    <section class="custom-section custom-section--days" id="days">
      <header class="custom-section__head">
        <h2 class="custom-section__title">Giorni e tappe</h2>
        <p class="custom-section__hint">Trascina ⋮⋮ per riordinare. Cerca luoghi con OpenStreetMap.</p>
      </header>
      <div class="custom-days">${dayBlocks}</div>
    </section>
  `;
}

function renderContent(ctx, activeTab = 'stays') {
  const sections = {
    stays: renderStaysSection(ctx),
    wishlist: renderWishlistSection(ctx),
    days: renderDaysSection(ctx),
  };

  return `
    <article class="customize-page">
      <h1 class="customize-page__title">Personalizza viaggio</h1>
      <nav class="customize-nav" aria-label="Sezioni">${renderCustomizeNav(ctx.tripId, activeTab)}</nav>
      ${sections[activeTab] ?? sections.stays}
    </article>
  `;
}

function findStop(ctx, stopId) {
  for (const day of ctx.days) {
    const stop = (day.stops ?? []).find((s) => s.id === stopId);
    if (stop) return { day, stop };
  }
  return null;
}

function showAddForm(area, html) {
  if (!area) return;
  const editor = area.querySelector('.custom-editor, #stay-editor, #wishlist-editor');
  const trigger = area.querySelector('[data-show-add-form]');
  if (editor) {
    editor.innerHTML = html;
    editor.hidden = false;
  }
  if (trigger) {
    trigger.hidden = true;
    trigger.setAttribute('aria-expanded', 'true');
  }
}

function hideAddForm(area) {
  if (!area) return;
  const editor = area.querySelector('.custom-editor, #stay-editor, #wishlist-editor');
  const trigger = area.querySelector('[data-show-add-form]');
  if (editor) {
    editor.innerHTML = '';
    editor.hidden = true;
  }
  if (trigger) {
    trigger.hidden = false;
    trigger.setAttribute('aria-expanded', 'false');
  }
}

async function syncCtx(ctx) {
  await refreshLiveData({ forceNetwork: true });
  const fresh = getContext();
  if (fresh) Object.assign(ctx, fresh);
}

function bindPlaceFields(container, country) {
  container.querySelectorAll('[data-place-name]').forEach((input) => {
    bindPlaceAutocomplete(input, { country, form: input.closest('form') });
  });
  container.querySelectorAll('form').forEach((form) => {
    if (form.querySelector('[data-place-name], [name="mapsUrl"], [data-place-map-picker]')) {
      bindPlaceEditor(form, { country });
    }
  });
}

function bindCustomize(container, ctx) {
  const country = ctx.trip.country ?? '';

  const rerender = async () => {
    const scrollY = window.scrollY;
    const activeTab = getActiveCustomizeTab();
    await syncCtx(ctx);
    refreshTripBases(container, resolveTripBases(ctx.live), ctx.tripId, ctx.trip);
    container.querySelector('#trip-main').innerHTML = renderContent(ctx, activeTab);
    bindCustomize(container, ctx);
    bindPlaceFields(container, country);
    window.scrollTo(0, scrollY);
  };

  bindPlaceFields(container, country);

  const main = container.querySelector('#trip-main') ?? container;

  customizeListenersAbort?.abort();
  customizeListenersAbort = new AbortController();
  const { signal } = customizeListenersAbort;

  main.querySelectorAll('[data-show-add-form]').forEach((btn) => {
    btn.addEventListener(
      'click',
      () => {
        const area = btn.closest('[data-add-area]');
        const kind = area?.dataset.addArea;
        if (kind === 'stay') showAddForm(area, stayForm());
        else if (kind === 'wishlist') showAddForm(area, wishlistForm());
        else if (kind === 'stop') {
          const dayId = area.dataset.dayId;
          const day = ctx.days.find((d) => d.id === dayId);
          if (day) showAddForm(area, stopForm({ instanceDayId: dayId }, day.date));
        }
        bindPlaceFields(main, country);
      },
      { signal },
    );
  });

  main.addEventListener(
    'click',
    (e) => {
      const cancelStay = e.target.closest('[data-cancel-stay-form]');
      if (cancelStay) {
        hideAddForm(main.querySelector('[data-add-area="stay"]'));
        return;
      }
      const cancelWishlist = e.target.closest('[data-cancel-wishlist-form]');
      if (cancelWishlist) {
        hideAddForm(main.querySelector('[data-add-area="wishlist"]'));
        return;
      }
      const cancelStop = e.target.closest('[data-cancel-stop-form]');
      if (cancelStop) {
        hideAddForm(cancelStop.closest('[data-add-area="stop"]'));
      }
    },
    { signal },
  );

  main.addEventListener(
    'submit',
    async (e) => {
    const stayFormEl = e.target.closest('[data-form="stay"]');
    if (stayFormEl) {
      e.preventDefault();
      if (!canEdit()) return;
      const fd = new FormData(stayFormEl);
      const bases = resolveTripBases(ctx.live);
      const stayId = String(fd.get('id') || '').trim();
      const existing = stayId ? bases.find((b) => b.id === stayId) : null;
      const coords = parseCoordsFromForm(fd);
      const costRaw = fd.get('costEur');
      try {
        await upsertInstanceBase(ctx.instance.id, {
          id: stayId || undefined,
          name: String(fd.get('name')).trim(),
          subtitle: String(fd.get('subtitle')).trim() || null,
          dateStart: fd.get('dateStart'),
          dateEnd: fd.get('dateEnd'),
          mapsUrl: String(fd.get('mapsUrl')).trim() || null,
          orderIdx: existing?.orderIdx ?? bases.length,
          lat: coords.lat,
          lng: coords.lng,
          costEur: costRaw,
          expenseId: existing?.expenseId ?? null,
        });
        invalidateInstanceMapCache(ctx.instance.id);
        await rerender();
      } catch (err) {
        alert(err.message);
      }
      return;
    }

    const wishlistFormEl = e.target.closest('[data-form="wishlist"]');
    if (wishlistFormEl) {
      e.preventDefault();
      if (!canEdit()) return;
      const fd = new FormData(wishlistFormEl);
      const items = ctx.live.wishlist ?? [];
      const itemId = String(fd.get('id') || '').trim();
      const coords = parseCoordsFromForm(fd);
      try {
        await upsertWishlistItem(ctx.instance.id, {
          id: itemId || undefined,
          name: String(fd.get('name')).trim(),
          notes: String(fd.get('notes')).trim() || null,
          lat: coords.lat,
          lng: coords.lng,
          placeId: String(fd.get('placeId') || '').trim() || null,
          placeMeta: parsePlaceMetaFromForm(fd) ?? {},
          mapsUrl: String(fd.get('mapsUrl') || '').trim() || null,
          showOnMap: fd.get('showOnMap') === 'on',
          orderIdx: itemId ? items.find((i) => i.id === itemId)?.orderIdx ?? items.length : items.length,
        });
        invalidateInstanceMapCache(ctx.instance.id);
        await rerender();
      } catch (err) {
        alert(err.message);
      }
      return;
    }

    const stopFormEl = e.target.closest('[data-form="stop"]');
    if (stopFormEl) {
      e.preventDefault();
      if (!canEdit()) return;
      const fd = new FormData(stopFormEl);
      const dayId = fd.get('instanceDayId');
      const day = ctx.days.find((d) => d.id === dayId);
      const coords = parseCoordsFromForm(fd);
      const stopId = fd.get('id') || undefined;
      const existing = stopId ? findStop(ctx, stopId)?.stop : null;
      try {
        await upsertInstanceStop(ctx.instance.id, dayId, {
          id: stopId,
          name: String(fd.get('name')).trim(),
          description: String(fd.get('description')).trim() || null,
          carTime: String(fd.get('carTime')).trim() || null,
          walkInfo: String(fd.get('walkInfo')).trim() || null,
          costInfo: String(fd.get('costInfo')).trim() || null,
          mapsUrl: String(fd.get('mapsUrl')).trim() || null,
          warning: String(fd.get('warning')).trim() || null,
          lat: coords.lat,
          lng: coords.lng,
          placeId: String(fd.get('placeId') || '').trim() || null,
          placeMeta: parsePlaceMetaFromForm(fd) ?? {},
          scheduledAt: scheduledAtFromDayTime(fd.get('dayDate'), String(fd.get('scheduledTime') || '')),
          orderIdx: existing?.order_idx ?? day?.stops?.length ?? 0,
        });
        invalidateInstanceMapCache(ctx.instance.id, toDayKey(day?.date));
        await rerender();
      } catch (err) {
        alert(err.message);
      }
      return;
    }

    const dayFormEl = e.target.closest('[data-day-form]');
    if (dayFormEl) {
      e.preventDefault();
      if (!canEdit()) return;
      const fd = new FormData(dayFormEl);
      try {
        await updateInstanceDay(ctx.instance.id, dayFormEl.dataset.dayForm, {
          title: String(fd.get('title')).trim(),
          ritmo: String(fd.get('ritmo')).trim(),
        });
        await rerender();
      } catch (err) {
        alert(err.message);
      }
    }
    },
    { signal },
  );

  container.querySelectorAll('.custom-list--sortable[data-sort-day]').forEach((list) => {
    if (!canEdit() || list.querySelector('.custom-list__empty')) return;
    bindSortableList(list, {
      onReorder: async (ids) => {
        await reorderInstanceStops(ctx.instance.id, list.dataset.sortDay, ids);
        await syncCtx(ctx);
      },
    });
  });

  const wishlistList = container.querySelector('#wishlist-sortable');
  if (wishlistList && canEdit() && !wishlistList.querySelector('.custom-list__empty')) {
    bindSortableList(wishlistList, {
      onReorder: async (ids) => {
        await reorderWishlist(ctx.instance.id, ids);
        await syncCtx(ctx);
      },
    });
  }

  container.querySelectorAll('[data-delete-stay]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      if (!confirm('Eliminare questo alloggio?')) return;
      try {
        await deleteInstanceBase(ctx.instance.id, btn.dataset.deleteStay);
        await rerender();
      } catch (err) {
        alert(err.message);
      }
    });
  });

  main.querySelectorAll('[data-edit-stay]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const base = resolveTripBases(ctx.live).find((b) => b.id === btn.dataset.editStay);
      const area = main.querySelector('[data-add-area="stay"]');
      if (area && base) {
        showAddForm(area, stayForm(base));
        bindPlaceFields(main, country);
      }
    });
  });

  main.querySelectorAll('[data-delete-wishlist]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      if (!confirm('Rimuovere dalla lista?')) return;
      try {
        await deleteWishlistItem(ctx.instance.id, btn.dataset.deleteWishlist);
        invalidateInstanceMapCache(ctx.instance.id);
        await rerender();
      } catch (err) {
        alert(err.message);
      }
    });
  });

  main.querySelectorAll('[data-edit-wishlist]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const item = (ctx.live.wishlist ?? []).find((i) => i.id === btn.dataset.editWishlist);
      const area = main.querySelector('[data-add-area="wishlist"]');
      if (area && item) {
        showAddForm(area, wishlistForm(item));
        bindPlaceFields(main, country);
      }
    });
  });

  main.querySelectorAll('[data-edit-day]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const form = container.querySelector(`[data-day-form="${btn.dataset.editDay}"]`);
      if (form) form.hidden = !form.hidden;
    });
  });

  main.querySelectorAll('[data-edit-stop]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const found = findStop(ctx, btn.dataset.editStop);
      const area = main.querySelector(`[data-add-area="stop"][data-day-id="${btn.dataset.dayId}"]`);
      if (found && area) {
        showAddForm(area, stopForm({ ...found.stop, instanceDayId: btn.dataset.dayId }, found.day.date));
        bindPlaceFields(main, country);
      }
    });
  });

  main.querySelectorAll('[data-delete-stop]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      if (!confirm('Eliminare questa tappa?')) return;
      try {
        await deleteInstanceStop(ctx.instance.id, btn.dataset.deleteStop);
        await rerender();
      } catch (err) {
        alert(err.message);
      }
    });
  });
}

function scrollToCustomizeTab(tab) {
  if (!tab) return;
  const map = { stays: 'stays', wishlist: 'wishlist', days: 'days' };
  requestAnimationFrame(() => {
    document.getElementById(map[tab] ?? tab)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
}

export function renderCustomize(container, ctx, tab = 'stays') {
  container.innerHTML = renderTripShell({
    trip: ctx.trip,
    tripId: ctx.tripId,
    activeTab: 'customize',
    members: ctx.live.members,
    bases: resolveTripBases(ctx.live),
    mainHtml: renderContent(ctx, tab),
  });
  bindTripShell(container, {
    tripId: ctx.tripId,
    instanceId: ctx.instance.id,
    tripTitle: ctx.trip.title,
  });
  bindCustomize(container, ctx);
  bindPlaceFields(container, ctx.trip.country ?? '');
  scrollToCustomizeTab(tab);
}

export function updateCustomizeMain(container, ctx, tab = getActiveCustomizeTab()) {
  const main = container.querySelector('#trip-main');
  if (!main) return;
  main.innerHTML = renderContent(ctx, tab);
  bindCustomize(container, ctx);
  bindPlaceFields(container, ctx.trip.country ?? '');
}
